import { CompositeScreenProps, NavigationContainerRefWithCurrent } from '@react-navigation/native'
import { links, platform } from 'constants'
import React, { useEffect, useMemo, useState } from 'react'
import { SafeAreaView, StyleSheet } from 'react-native'

import { BottomSheetNavbar } from 'partials/BottomSheetNavbar'
import { ChatRoom } from 'partials/ChatRoom'
import { Page } from 'partials/Page'

import { Avatar } from 'widgets/Avatar'
import { Box } from 'widgets/Box'
import { Icon } from 'widgets/Icon'
import { LoadingSpinner } from 'widgets/LoadingSpinner'
import { Modal } from 'widgets/Modal'
import { OptionItem } from 'widgets/OptionsDropdown/subcomponents/OptionItem'
import { Pressable } from 'widgets/Pressable'
import { Separator } from 'widgets/Separator'
import { Text } from 'widgets/Text'
import { Title } from 'widgets/Title'
import { UpspaceRowMembers } from 'widgets/UpspaceRowMembers'

import { useCompletion } from 'hooks/useCompletion'
import { useCompletionComment } from 'hooks/useCompletionComment'
import { useCompletionRoom } from 'hooks/useCompletionRoom'
import { useDMRoom } from 'hooks/useDMRoom'
import { useGroup } from 'hooks/useGroup'
import { useNavigateBackOrHome } from 'hooks/useNavigateBackOrHome'
import { usePost } from 'hooks/usePost'
import { usePostComment } from 'hooks/usePostComment'
import { usePostRoom } from 'hooks/usePostRoom'
import { useSubscriptions } from 'hooks/useSubscriptions'

import { openReportForm } from 'utils/links/links'
import commonStyles from 'utils/styles/commonStyles'
import { Toast } from 'utils/toast'

import { useCommentModal } from 'contexts/CommentModalContext'
import { useFocusMode } from 'contexts/FocusModeContext'

import { Room, RoomTypes } from 'types/chat'
import { Subscription } from 'types/subscription'
import { UserType } from 'types/user'

const styles = StyleSheet.create({
  safeArea: { flex: 0, backgroundColor: 'white' },
})

const menuOptions = ['Report Message']

interface ChatRoomPageProps extends CompositeScreenProps<any, any> {
  chatroomId?: string
  commentPostId?: string
  commentCompletionId?: string
  isBottomSheetPresentation?: boolean
  drawerNavigationRef?: NavigationContainerRefWithCurrent<ReactNavigation.RootParamList>
}

export const ChatRoomPage = ({
  navigation,
  route,
  chatroomId,
  commentPostId,
  commentCompletionId,
  isBottomSheetPresentation = false,
  drawerNavigationRef,
}: ChatRoomPageProps) => {
  const commentModalRef = useCommentModal()
  const navigateBackOrHome = useNavigateBackOrHome()
  const focusMode = useFocusMode()
  const isFocusMode = useMemo(() => focusMode.group !== undefined, [focusMode.group])

  const roomId = route?.params?.roomId ?? chatroomId
  const postId = route?.params?.postId ?? commentPostId
  const completionId = route?.params?.completionId ?? commentCompletionId

  const { data: post, isLoading: isPostLoading } = usePost(postId)
  const { data: completion, isLoading: isCompletionLoading } = useCompletion(completionId)

  const { addComment: addPostComment } = usePostComment({ postId })
  const { addComment: addCompletionComment } = useCompletionComment({ completionId })
  const {
    data: dmRoom,
    error: dmRoomError,
    isFetched: isDMRoomFetched,
    isLoading: isDMRoomLoading,
  } = useDMRoom({ roomId })

  const {
    data: postRoom,
    error: postRoomError,
    isFetched: isPostRoomFetched,
    isLoading: isPostRoomLoading,
  } = usePostRoom({ postId })

  const {
    data: completionRoom,
    error: completionRoomError,
    isFetched: isCompletionRoomFetched,
    isLoading: isCompletionRoomLoading,
  } = useCompletionRoom({ completionId })

  const room = useMemo<Room | null | undefined>(() => {
    if (postId) {
      return postRoom
    }
    if (completionId) {
      return completionRoom
    }
    return dmRoom
  }, [postId, completionId, dmRoom, postRoom, completionRoom])

  const error = useMemo<any>(() => {
    if (postId) {
      return postRoomError
    }
    if (completionId) {
      return completionRoomError
    }
    return dmRoomError
  }, [completionId, completionRoomError, dmRoomError, postId, postRoomError])

  const isFetched = useMemo<boolean>(() => {
    if (postId) {
      return isPostRoomFetched
    }
    if (completionId) {
      return isCompletionRoomFetched
    }
    return isDMRoomFetched
  }, [completionId, isCompletionRoomFetched, isDMRoomFetched, isPostRoomFetched, postId])

  const subscription: Subscription = route?.params?.subscription
  const { data: members, isLoading: isSubscriptionsLoading } = useSubscriptions({
    trainerId: subscription?.program?.trainer?.id,
  })

  const [isOptionsModalVisible, setIsOptionsModalVisible] = useState(false)
  const [reportedAccount, setReportedAccount] = useState<UserType | undefined>()

  const onMessageLongPress = (reportedUser: UserType) => {
    setIsOptionsModalVisible(true)
    setReportedAccount(reportedUser)
  }

  const handleCloseOptionsMenuPress = (): void => {
    setIsOptionsModalVisible(false)
  }

  const handleOnSelect = (index: number) => {
    setIsOptionsModalVisible(false)
    if (index === 0) {
      if (room?.avatarUsers.length === 2) {
        openReportForm(room?.avatarUsers[0], room?.avatarUsers[1])
      } else {
        openReportForm(room?.avatarUsers[0], reportedAccount !== undefined ? reportedAccount : null)
      }
    }
  }

  useEffect(() => {
    if (error || !roomId || (isFetched && !room)) {
      Toast.error('Chat room not found', "We couldn't find that chat room. Please try again.")
      navigateBackOrHome()
    }
  }, [error, navigateBackOrHome, roomId, isFetched, room])

  const navigateToProfile = () => {
    navigation.navigate(!isFocusMode ? links.PROFILE_STACK : links.GROUP_STACK, {
      screen: links.MEMBER_PROFILE_PAGE,
      params: { username: room?.avatarUsers[1].username },
    })
  }

  const { data: group, isLoading: isGroupLoading } = useGroup({
    groupId: room?.reference?.ids[0],
  })

  const incrementCommentCount = () => {
    if (room?.reference?.type === RoomTypes.DIRECT || room?.reference?.type === RoomTypes.PROGRAM) {
      return
    }
    room?.reference?.type === RoomTypes.POST && addPostComment.mutateAsync()
    room?.reference?.type === RoomTypes.COMPLETION && addCompletionComment.mutateAsync()
  }

  const getRoomTitle = (): string => {
    switch (room?.reference?.type) {
      case RoomTypes.DIRECT:
        return room?.name ?? ''
      case RoomTypes.PROGRAM:
        return group?.title ?? ''
      case RoomTypes.POST:
        return post
          ? `${post.commentsCount} comment${post.commentsCount === 1 ? '' : 's'}`
          : 'Comments'
      case RoomTypes.COMPLETION:
        return completion
          ? `${completion.commentsCount} comment${completion.commentsCount === 1 ? '' : 's'}`
          : 'Comments'
      default:
        return ''
    }
  }

  const isLoading = useMemo(
    () =>
      isPostLoading ||
      isCompletionLoading ||
      isDMRoomLoading ||
      isPostRoomLoading ||
      isCompletionRoomLoading ||
      isSubscriptionsLoading ||
      isGroupLoading,
    [
      isCompletionLoading,
      isCompletionRoomLoading,
      isDMRoomLoading,
      isPostLoading,
      isPostRoomLoading,
      isGroupLoading,
      isSubscriptionsLoading,
    ],
  )

  return (
    <Page
      isOnBottomSheet
      navigation={navigation}
      enableKeyboardAvoidingView={!isBottomSheetPresentation}
      isLoading={isLoading}
    >
      <SafeAreaView style={styles.safeArea} />

      <Modal
        isVisible={isOptionsModalVisible}
        onClose={handleCloseOptionsMenuPress}
        variant='fullscreenModal'
      >
        {menuOptions.map((option, index) => {
          return (
            <OptionItem
              key={option}
              option={option}
              isCancelItem={false}
              onSelect={() => {
                handleOnSelect(index)
              }}
            />
          )
        })}
        <OptionItem isCancelItem onSelect={handleCloseOptionsMenuPress} />
      </Modal>

      {isBottomSheetPresentation && (
        <BottomSheetNavbar
          title={getRoomTitle()}
          onClose={() => {
            commentModalRef?.current?.dismiss()
          }}
        />
      )}

      {!isBottomSheetPresentation && (
        <>
          <Box
            testID='HeaderContainer'
            flexDirection='row'
            height={!platform.IS_WEB ? 55 : 85}
            backgroundColor='white'
            margin={{ top: !platform.IS_WEB ? -10 : undefined }}
            position='relative'
            zIndex={2}
            align='center'
            justifyContent='space-between'
          >
            <Box flex={1} flexDirection='row' testID='TitleAndBackButtonColumn'>
              <Box flex={1} testID='TitleAndBackButtonContainer' flexDirection='row' align='center'>
                <Box
                  testID='BackButtonContainer'
                  width={24}
                  height={24}
                  margin={{ horizontal: 'md' }}
                  justifyContent='center'
                  align='center'
                >
                  <Pressable onPress={navigateBackOrHome}>
                    <Icon name='arrow-left' color='black' size={24} />
                  </Pressable>
                </Box>

                <Box testID='TitleContainer' flexDirection='row' style={commonStyles.flexGrow}>
                  <Title size='sm' bold numberOfLines={1}>
                    {getRoomTitle()}
                  </Title>
                </Box>
                <Box margin='smd'>
                  {room?.reference.type === RoomTypes.DIRECT && (
                    <Pressable onPress={navigateToProfile}>
                      <Avatar width={26} height={26} image={room.avatarUsers[1].avatar} />
                    </Pressable>
                  )}
                  {room?.reference.type === RoomTypes.PROGRAM && subscription && members && (
                    <UpspaceRowMembers subscription={subscription} itemSize={26} />
                  )}
                </Box>
              </Box>
            </Box>
          </Box>
          <Separator />
        </>
      )}

      {room ? (
        <ChatRoom
          roomType={room?.reference?.type ?? RoomTypes.DIRECT}
          roomId={roomId}
          onMessageLongPress={onMessageLongPress}
          onMessageSend={incrementCommentCount}
          isBottomSheetPresentation={isBottomSheetPresentation}
          drawerNavigationRef={drawerNavigationRef}
        />
      ) : (
        <Box display='flex' flex={1} justifyContent='center' align='center' height='100%'>
          <LoadingSpinner />
          <Box padding={{ vertical: 'sm' }}>
            <Text>Loading...</Text>
          </Box>
        </Box>
      )}
    </Page>
  )
}
